import { useState } from 'react';
import Header from '@/components/Header';
import { useAuthStore } from '@/store/useAuthStore';
import { ROUTES } from '@/utils/constants';
import { Link } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.amebogpt.com.ng';

const DocsPage = () => {
  const { firebaseUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'quickstart' | 'reference' | 'examples'>('quickstart');

  const codeExamples = {
    curl: `curl -X POST "${API_BASE_URL}/tts" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "text": "Hello, welcome to ameboGPT!",
    "language": "english",
    "speaker_name": "chimobi",
    "temperature": 0.1,
    "repetition_penalty": 1.1
  }'`,
    
    javascript: `const response = await fetch('${API_BASE_URL}/tts', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    text: 'Hello, welcome to ameboGPT!',
    language: 'english',
    speaker_name: 'chimobi',
    temperature: 0.1,
    repetition_penalty: 1.1
  })
});

const data = await response.json();
console.log('Audio URL:', data.audio_url);`,
    
    python: `import requests

url = "${API_BASE_URL}/tts"
headers = {
    "Content-Type": "application/json",
    "X-API-Key": "YOUR_API_KEY"
}
data = {
    "text": "Hello, welcome to ameboGPT!",
    "language": "english",
    "speaker_name": "chimobi",
    "temperature": 0.1,
    "repetition_penalty": 1.1
}

response = requests.post(url, json=data, headers=headers)
result = response.json()
print(f"Audio URL: {result['audio_url']}")`,
  };

  return (
    <div className="main-page-wrapper">
      <Header />
      
      <section className="pt-150 lg-pt-80 pb-100 lg-pb-80 mt-30 lg-mt-20">
        <div className="container">
          <div className="text-center mb-60">
            <div className="title-one">
              <h2>Developer Documentation</h2>
            </div>
            <p className="fs-20 text-dark mt-20">
              Integrate ameboGPT's Nigerian Text-to-Speech API into your applications
            </p>
          </div>

          {!firebaseUser && (
            <div className="alert alert-info border-30 mb-40" role="alert">
              <strong>👋 New to ameboGPT?</strong> <Link to={ROUTES.SIGNUP} className="text-primary">Sign up for free</Link> to get your API key and start building!
            </div>
          )}

          {/* Tabs */}
          <div className="card-style-one border-30 bg-white mb-40">
            <ul className="nav nav-tabs border-0 p-3" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'quickstart' ? 'active' : ''}`}
                  onClick={() => setActiveTab('quickstart')}
                >
                  Quick Start
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'reference' ? 'active' : ''}`}
                  onClick={() => setActiveTab('reference')}
                >
                  API Reference
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'examples' ? 'active' : ''}`}
                  onClick={() => setActiveTab('examples')}
                >
                  Code Examples
                </button>
              </li>
            </ul>
          </div>

          {/* Quick Start Tab */}
          {activeTab === 'quickstart' && (
            <div className="card-style-one border-30 bg-white p-4">
              <h3 className="mb-4">🚀 Quick Start Guide</h3>
              
              <div className="mb-4">
                <h4 className="mb-3">1. Get Your API Key</h4>
                {firebaseUser ? (
                  <p className="text-dark mb-3">
                    You're logged in! <Link to={ROUTES.API_KEYS} className="text-primary">Go to Dashboard</Link> to create your API key.
                  </p>
                ) : (
                  <p className="text-dark mb-3">
                    <Link to={ROUTES.SIGNUP} className="btn-one">Sign up for free</Link> to get your API key.
                  </p>
                )}
              </div>

              <div className="mb-4">
                <h4 className="mb-3">2. Make Your First Request</h4>
                <p className="text-dark mb-3">
                  Use your API key in the <code>X-API-Key</code> header:
                </p>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{codeExamples.curl}</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">3. Get Available Voices</h4>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{`curl "${API_BASE_URL}/voices/all"`}</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">4. Response Format</h4>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{`{
  "request_id": "uuid-here",
  "audio_url": "/audio/filename.wav",
  "text": "Your text here",
  "speaker": "chimobi"
}`}</code>
                </pre>
              </div>
            </div>
          )}

          {/* API Reference Tab */}
          {activeTab === 'reference' && (
            <div className="card-style-one border-30 bg-white p-4">
              <h3 className="mb-4">📚 API Reference</h3>
              
              <div className="mb-4">
                <h4 className="mb-3">Base URL</h4>
                <p className="text-dark">
                  <code>{API_BASE_URL}</code>
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">Authentication</h4>
                <p className="text-dark mb-3">
                  Include your API key in the request header:
                </p>
                <pre className="bg-light p-3 rounded border">
                  <code>X-API-Key: YOUR_API_KEY</code>
                </pre>
                <p className="text-dark mt-3">
                  Or use Bearer token format:
                </p>
                <pre className="bg-light p-3 rounded border">
                  <code>Authorization: Bearer YOUR_API_KEY</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">POST /tts - Generate Speech</h4>
                <p className="text-dark mb-3">Convert text to speech with Nigerian accents.</p>
                
                <h5 className="mt-3 mb-2">Request Body:</h5>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{`{
  "text": "string (required)",
  "language": "english | hausa | igbo | yoruba",
  "speaker_name": "string (required)",
  "temperature": 0.1,
  "repetition_penalty": 1.1
}`}</code>
                </pre>

                <h5 className="mt-3 mb-2">Response:</h5>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{`{
  "request_id": "uuid",
  "audio_url": "/audio/file.wav",
  "text": "original text",
  "speaker": "speaker name"
}`}</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">GET /voices/all - List All Voices</h4>
                <p className="text-dark mb-3">Get all available voices organized by language.</p>
                <pre className="bg-light p-3 rounded border">
                  <code>{`GET ${API_BASE_URL}/voices/all`}</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">Rate Limits</h4>
                <ul className="text-dark">
                  <li><strong>Free:</strong> 10,000 characters/month, 10 requests/minute</li>
                  <li><strong>Starter:</strong> 100,000 characters/month, 30 requests/minute</li>
                  <li><strong>Professional:</strong> 500,000 characters/month, 100 requests/minute</li>
                  <li><strong>Business:</strong> 2,000,000 characters/month, 300 requests/minute</li>
                  <li><strong>Enterprise:</strong> Unlimited, 1,000 requests/minute</li>
                </ul>
              </div>
            </div>
          )}

          {/* Code Examples Tab */}
          {activeTab === 'examples' && (
            <div className="card-style-one border-30 bg-white p-4">
              <h3 className="mb-4">💻 Code Examples</h3>
              
              <div className="mb-4">
                <h4 className="mb-3">cURL</h4>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{codeExamples.curl}</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">JavaScript (Fetch API)</h4>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{codeExamples.javascript}</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">Python (requests)</h4>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{codeExamples.python}</code>
                </pre>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">Node.js (axios)</h4>
                <pre className="bg-light p-3 rounded border" style={{ overflowX: 'auto' }}>
                  <code>{`const axios = require('axios');

const response = await axios.post('${API_BASE_URL}/tts', {
  text: 'Hello, welcome to ameboGPT!',
  language: 'english',
  speaker_name: 'chimobi',
  temperature: 0.1,
  repetition_penalty: 1.1
}, {
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

console.log('Audio URL:', response.data.audio_url);`}</code>
                </pre>
              </div>
            </div>
          )}

          {/* Interactive API Docs Link */}
          <div className="card-style-one border-30 bg-white p-4 mt-40 text-center">
            <h4 className="mb-3">🔐 Interactive API Documentation</h4>
            <p className="text-dark mb-4">
              Access the full interactive Swagger UI documentation with your API key
            </p>
            {firebaseUser ? (
              <Link to={ROUTES.API_KEYS} className="btn-one">
                Get API Key & Access Docs
              </Link>
            ) : (
              <Link to={ROUTES.SIGNUP} className="btn-one">
                Sign Up & Get API Key
              </Link>
            )}
            <p className="text-dark mt-3 fs-14">
              Or visit: <a href={`${API_BASE_URL}/docs?api_key=YOUR_KEY`} target="_blank" rel="noopener noreferrer" className="text-primary">{API_BASE_URL}/docs</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocsPage;
